import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { localBusinessSchema } from '../data/schema';
import emailjs from '@emailjs/browser';
import '../css/Contact.css';
import Footer from './footer/Footer';
import Header from '../components/Header';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        serviceType: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Configuration EmailJS
    const EMAILJS_SERVICE_ID = 'service_yeelmvu'; 
    const EMAILJS_TEMPLATE_ID = 'template_tay5fkb'; 
    const EMAILJS_PUBLIC_KEY = 'JAExh2BmxCEgxx9oA';

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Préparer les données pour EmailJS
            const templateParams = {
                from_name: formData.name,
                from_email: formData.email,
                from_phone: formData.phone,
                service_type: formData.serviceType,
                subject: formData.subject || 'Nouvelle demande de devis',
                message: formData.message,
                to_email: 'louloupassion1505@gmail.com',
                reply_to: formData.email || 'ccdebarras14@gmail.com'
            };

            // Envoyer l'email via EmailJS
            const response = await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                templateParams,
                EMAILJS_PUBLIC_KEY
            );

            console.log('Email envoyé avec succès:', response);
            setSubmitStatus('success');
            
            // Réinitialiser le formulaire
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                serviceType: ''
            });

        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact - CC Débarras 14 | Devis gratuit débarras Deauville</title>
                <meta name="description" content="Contactez CC Débarras 14 pour un devis gratuit. Service de débarras professionnel à Deauville. Appelez le 07 67 56 72 54 ou utilisez notre formulaire." />
                <meta name="keywords" content="contact débarras Deauville, devis gratuit, déménagement Normandie, téléphone débarras, formulaire contact" />
                <link rel="canonical" href="https://ccdebarras14.fr/contact" />
                
                {/* Open Graph */}
                <meta property="og:title" content="Contact - CC Débarras 14" />
                <meta property="og:description" content="Contactez-nous pour un devis gratuit de débarras à Deauville et environs" />
                <meta property="og:url" content="https://ccdebarras14.fr/contact" />
                <meta property="og:type" content="website" />
                
                {/* Données structurées pour la page contact */}
                <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "name": "Contact CC Débarras 14",
                    "description": "Page de contact pour CC Débarras 14 - Service de débarras professionnel",
                    "url": "https://ccdebarras14.fr/contact",
                    "mainEntity": { "@id": "https://ccdebarras14.fr/#business" }
                })}
                </script>
                <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
            </Helmet>
            
            <Header />
            
            <div className="contact-component">
                <div className="contact-content">
                    {/* Hero Section */}
                    <div className="contact-hero-section">
                        <div className="contact-hero-content">
                            <h1>Contactez-nous</h1>
                            <p>Besoin d'un débarras ? Nous sommes là pour vous accompagner dans votre projet.</p>
                            <p>Devis gratuit et intervention rapide sur Deauville et environs.</p>
                        </div>
                        <div className="contact-hero-image">
                            <img 
                                src="/img/contact/vide-maison.jpg" 
                                alt="Équipe CC Débarras 14 en intervention" 
                                className="contact-hero-img"
                            />
                        </div>
                    </div>

                    {/* Contact rapide */}
                    <div className="contact-quick-section">
                        <h2>Contact rapide</h2>
                        <div className="contact-quick-cards">
                            <div className="contact-quick-card">
                                <div className="contact-quick-card-header">
                                    <div className="contact-quick-icon">
                                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15.5562 14.5477L15.1007 15.0272C15.1007 15.0272 14.0181 16.167 11.0631 13.0559C8.10812 9.94484 9.1907 8.80507 9.1907 8.80507L9.47752 8.50311C10.1841 7.75924 10.2507 6.56497 9.63424 5.6931L8.37326 3.90961C7.61028 2.8305 6.13596 2.68795 5.26145 3.60864L3.69185 5.26114C3.25823 5.71766 2.96765 6.30945 3.00289 6.96594C3.09304 8.64546 3.81071 12.259 7.81536 16.4752C12.0621 20.9462 16.0468 21.1239 17.6763 20.9631C18.1917 20.9122 18.6399 20.6343 19.0011 20.254L20.4217 18.7584C21.3806 17.7489 21.1102 16.0182 19.8833 15.312L17.9728 14.2123C17.1672 13.7486 16.1858 13.8848 15.5562 14.5477Z" fill="#a53f2b"/>
                                        </svg>
                                    </div>
                                    <div className="contact-quick-badge contact-quick-urgent-badge">Urgent</div>
                                </div>
                                <div className="contact-quick-card-content">
                                    <h3>Appelez-nous</h3>
                                    <p>Pour une intervention rapide ou un devis urgent. Disponible 7j/7 pour vos urgences</p>
                                    <div className="contact-quick-stats">
                                        <span className="contact-quick-stat">
                                            <strong>Disponible</strong> 7j/7
                                        </span>
                                        <span className="contact-quick-stat">
                                            <strong>Réponse</strong> immédiate
                                        </span>
                                    </div>
                                </div>
                                <div className="contact-quick-card-footer">
                                    <a href="tel:0767567254" className="contact-phone-link">
                                        07 67 56 72 54 →
                                    </a>
                                </div>
                            </div>

                            <div className="contact-quick-card">
                                <div className="contact-quick-card-header">
                                    <div className="contact-quick-icon">
                                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12ZM16 12V13.5C16 14.8807 17.1193 16 18.5 16V16C19.8807 16 21 14.8807 21 13.5V12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21H16" stroke="#a53f2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                                    </div>
                                    <div className="contact-quick-badge">Pratique</div>
                                </div>
                                <div className="contact-quick-card-content">
                                    <h3>Email</h3>
                                    <p>Pour une demande détaillée avec photos et informations complètes de votre projet</p>
                                    <div className="contact-quick-stats">
                                        <span className="contact-quick-stat">
                                            <strong>Réponse</strong> sous 24h
                                        </span>
                                        <span className="contact-quick-stat">
                                            <strong>Devis</strong> détaillé
                                        </span>
                                    </div>
                                </div>
                                <div className="contact-quick-card-footer">
                                    <a href="mailto:ccdebarras14@gmail.com" className="contact-email-link">
                                        ccdebarras14@gmail.com →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Formulaire de contact */}
                    <div className="contact-form-section">
                        <div className="contact-form-header">
                            <h2>Demande de devis gratuit</h2>
                            <p>Remplissez ce formulaire pour recevoir votre devis personnalisé sous 24h</p>
                        </div>

                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="contact-form-row">
                                <div className="contact-form-group">
                                    <label htmlFor="name">Nom complet *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Votre nom et prénom"
                                    />
                                </div>
                                <div className="contact-form-group">
                                    <label htmlFor="phone">Téléphone *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="06 12 34 56 78"
                                    />
                                </div>
                            </div>

                            <div className="contact-form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="votre.email@exemple.fr"
                                />
                            </div>

                            <div className="contact-form-group">
                                <label htmlFor="serviceType">Type de service</label>
                                <select
                                    id="serviceType"
                                    name="serviceType"
                                    value={formData.serviceType}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Sélectionnez un service</option>
                                    <option value="debarras-maison">Débarras de maison</option>
                                    <option value="debarras-appartement">Débarras d'appartement</option>
                                    <option value="debarras-cave">Débarras de cave</option>
                                    <option value="debarras-grenier">Débarras de grenier</option>
                                    <option value="debarras-garage">Débarras de garage</option>
                                    <option value="debarras-professionnel">Débarras professionnel</option>
                                    <option value="succession">Succession / décès</option>
                                    <option value="demenagement">Aide au déménagement</option>
                                    <option value="autre">Autre</option>
                                </select>
                            </div>

                            <div className="contact-form-group">
                                <label htmlFor="subject">Objet de votre demande</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    placeholder="Ex: Débarras suite à succession"
                                />
                            </div>

                            <div className="contact-form-group">
                                <label htmlFor="message">Description de votre projet *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows="5"
                                    placeholder="Décrivez votre projet : type de logement, surface approximative, urgence, date souhaitée, objets de valeur potentiels..."
                                ></textarea>
                            </div>

                            <div className="contact-form-submit">
                                <button 
                                    type="submit" 
                                    className="contact-submit-button"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                                </button>
                                <p className="contact-form-note">
                                    * Champs obligatoires. Vos données sont protégées et ne seront jamais partagées.
                                </p>
                            </div>

                            {submitStatus === 'success' && (
                                <div className="contact-success-message">
                                    ✅ Votre demande a été envoyée avec succès ! Nous vous recontacterons sous 24h.
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="contact-error-message">
                                    ❌ Une erreur s'est produite lors de l'envoi. Veuillez réessayer ou nous appeler directement.
                                </div>
                            )}
                        </form>
                    </div>

                    {/* FAQ rapide */}
                    <div className="contact-faq-section">
                        <h2>Questions fréquentes</h2>
                        <div className="contact-faq-items">
                            <div className="contact-faq-item">
                                <h3>Le devis est-il vraiment gratuit ?</h3>
                                <p>Oui, notre devis est entièrement gratuit et sans engagement. Nous nous déplaçons pour évaluer votre projet sur place.</p>
                            </div>
                            <div className="contact-faq-item">
                                <h3>Dans quels délais intervenez-vous ?</h3>
                                <p>Nous pouvons généralement intervenir sous 48h pour un devis et sous une semaine pour la réalisation, selon l'urgence.</p>
                            </div>
                            <div className="contact-faq-item">
                                <h3>Que faites-vous des objets récupérés ?</h3>
                                <p>Les objets en bon état sont revendus sur nos plateformes (Leboncoin, Facebook). Le reste est trié et déposé en déchetterie.</p>
                            </div>
                            <div className="contact-faq-item">
                                <h3>Êtes-vous assurés ?</h3>
                                <p>Oui, nous sommes entièrement assurés responsabilité civile professionnelle pour toutes nos interventions.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Footer />
        </>
    );
};

export default Contact;